import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Buildings, ArrowRight, Check, Clock, Plus, CircleNotch, AppWindow, Truck, Power } from '@phosphor-icons/react';

import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';

const Integrations = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);

  // State for dynamic provider loading
  const [providers, setProviders] = useState([]);
  const [integrationStatuses, setIntegrationStatuses] = useState({});
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dynamic icon mapping for providers
  const getProviderIcon = (providerName) => {
    const iconMap = {
      'CCI LaundryCard': Buildings,
      'CCI FasCard': Buildings,
      'Curbside': Truck,
      'Stripe': Buildings,
      'Square': Buildings,
    };
    return iconMap[providerName] || AppWindow;
  };

  // Fetch providers and integration statuses
  useEffect(() => {
    const fetchProvidersAndStatuses = async () => {
      try {
        setLoading(true);

        const { getUserAccountId } = await import('../utils/accountUtils');
        const { accountId, error: accountIdError } = await getUserAccountId();
        if (accountIdError) throw new Error(accountIdError);

        if (accountId) {
          // Fetch all active providers
          const { data: providersData, error: providersError } = await supabase
            .from('api_providers')
            .select('*')
            .eq('is_active', true)
            .order('name');

          if (providersError) throw providersError;

          // Manually add Curbside integration
          const curbsideProvider = {
            id: 'curbside-manual',
            name: 'Curbside',
            slug: 'curbside',
            is_active: true
          };

          const allProviders = [...(providersData || []), curbsideProvider];
          setProviders(allProviders);

          // Fetch user's integration statuses
          const { data: integrationsData, error: integrationsError } = await supabase
            .from('v_integrations')
            .select('*')
            .eq('account_id', accountId);

          if (integrationsError && integrationsError.code !== 'PGRST116') throw integrationsError;

          // Fetch actual accounts_integrations for toggle functionality
          const { data: accountsIntegrationsData, error: accountsIntegrationsError } = await supabase
            .from('accounts_integrations')
            .select('*')
            .eq('account_id', accountId);

          if (accountsIntegrationsError && accountsIntegrationsError.code !== 'PGRST116') throw accountsIntegrationsError;
          setIntegrations(accountsIntegrationsData || []);

          // Check Curbside status separately
          const { data: curbsideData, error: curbsideError } = await supabase
            .from('curbside_settings')
            .select('integration_status')
            .eq('account_id', accountId)
            .maybeSingle();

          if (curbsideError && curbsideError.code !== 'PGRST116') throw curbsideError;

          // Build status map
          const statusMap = {};
          allProviders.forEach(provider => {
            if (provider.slug === 'curbside') {
              // Handle Curbside status manually
              if (!curbsideData) {
                statusMap[provider.slug] = 'not_started';
              } else {
                statusMap[provider.slug] = curbsideData.integration_status || 'not_started';
              }
            } else {
              // Handle other providers from v_integrations
            const integration = integrationsData?.find(i => i.provider_slug === provider.slug);
            if (!integration) {
              statusMap[provider.slug] = 'not_started';
            } else if (integration.auth_ready) {
              statusMap[provider.slug] = 'complete';
            } else {
              statusMap[provider.slug] = 'in_progress';
              }
            }
          });

          setIntegrationStatuses(statusMap);
        }
      } catch (err) {
        console.error('Error fetching providers and statuses:', err);
      } finally {
        setLoading(false);
      }
    };

    if (location.pathname === '/connect/apps' || location.pathname === '/integrations') {
      fetchProvidersAndStatuses();
    }
  }, [location.pathname, supabase]);

  // Function to get status display info
  const getStatusDisplay = (providerSlug) => {
    const status = integrationStatuses[providerSlug];

    switch(status) {
      case 'complete':
        return {
          label: 'Connected',
          icon: Check,
          className: 'bg-gray-900 text-white'
        };
      case 'in_progress':
        return {
          label: 'Setting Up',
          icon: Clock,
          className: 'bg-gray-100 text-gray-700 border border-gray-200'
        };
      case 'not_started':
      default:
        return {
          label: 'Connect',
          icon: Plus,
          className: 'bg-gray-50 text-gray-700 border border-gray-200'
        };
    }
  };

  // Toggle integration active status
  const toggleIntegrationStatus = async (integrationId, currentStatus) => {
    try {
      const newStatus = !currentStatus;

      const { error: updateError } = await supabase
        .from('accounts_integrations')
        .update({ is_active: newStatus })
        .eq('id', integrationId);

      if (updateError) throw updateError;

      // Update local state
      setIntegrations(prev => 
        prev.map(integration => 
          integration.id === integrationId 
            ? { ...integration, is_active: newStatus }
            : integration
        )
      );

    } catch (err) {
      console.error('Error toggling integration status:', err);
    }
  };

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.2,
        ease: [0, 0, 0.2, 1]
      }
    },
    hover: { 
      y: -4,
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.02)",
      transition: { 
        duration: 0.2,
        ease: [0, 0, 0.2, 1]
      }
    }
  };

  // If we're at the base route, show the list of integrations
  if (location.pathname === '/connect/apps' || location.pathname === '/integrations') {
    return (
      <motion.div 
        className="min-h-screen bg-gray-50 flex flex-col items-center"
        variants={pageVariants}
        initial="initial"
        animate="animate"
      >
        <div className="w-full max-w-4xl px-4">
          <main className="w-full mx-auto py-8">
            {!loading ? (
              <>
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                  variants={pageVariants}
                >
                  {providers.map((provider) => {
                    const statusDisplay = getStatusDisplay(provider.slug);
                    const StatusIcon = statusDisplay.icon;
                    const ProviderIcon = getProviderIcon(provider.name);

                    // Find the actual integration for this provider
                    const integration = integrations.find(int => int.provider_id === provider.id);
                    const isConnected = integrationStatuses[provider.slug] === 'complete';

                    return (
                      <motion.div
                        key={provider.id}
                        variants={cardVariants}
                        whileHover="hover"
                        className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-200"
                        >
                          <div className="p-6 flex flex-col h-full">
                            <div className="flex items-start justify-between mb-4">
                              <div className="bg-gray-50 p-2 rounded-full">
                                <ProviderIcon className="w-5 h-5 text-gray-700" />
                              </div>
                              <div className={`flex items-center text-xs font-medium px-3 py-1 rounded-full transition-colors ${statusDisplay.className}`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                <span>{statusDisplay.label}</span>
                              </div>
                            </div>

                            <h3 className="text-lg font-medium text-gray-900 mb-2">{provider.name}</h3>
                            <p className="text-gray-600 text-sm mb-6 flex-grow">
                              Connect with {provider.name} to streamline your operations
                            </p>

                          {/* Status Toggle for Connected Integrations */}
                          {isConnected && integration && (
                            <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center">
                                <Power className="w-4 h-4 text-gray-600 mr-2" />
                                <span className="text-sm font-medium text-gray-700">
                                  {integration.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleIntegrationStatus(integration.id, integration.is_active);
                                }}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 ${
                                  integration.is_active 
                                    ? 'bg-green-600' 
                                    : 'bg-gray-300'
                                }`}
                              >
                                <span
                                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition ${
                                    integration.is_active ? 'translate-x-5' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </div>
                          )}

                          <Link 
                            to={`/connect/apps/${provider.slug}`}
                            className="flex items-center text-gray-900 font-medium text-sm mt-auto group hover:text-gray-700"
                          >
                              <span>{
                                integrationStatuses[provider.slug] === 'complete' ? 'Manage' : 
                                integrationStatuses[provider.slug] === 'in_progress' ? 'View Progress' : 
                                'Setup'
                              }</span>
                              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                          </Link>
                          </div>
                      </motion.div>
                    );
                  })}
                </motion.div>

                {/* Empty state if no providers */}
                {providers.length === 0 && (
                  <motion.div 
                    variants={cardVariants}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center"
                  >
                    <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <AppWindow className="w-6 h-6 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No integrations available</h3>
                    <p className="text-gray-600 text-sm">
                      We're working on adding integrations. Check back soon.
                    </p>
                  </motion.div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <CircleNotch className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            )}
          </main>
        </div>
      </motion.div>
    );
  }

  // Otherwise, render the specific integration view through Outlet
  return <Outlet />;
};

export default Integrations; 